import { useState, useEffect } from 'react';

export default function QuarryEvent() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    // Add Schema.org JSON-LD for Event and FAQPage
    const eventSchema = {
      "@context": "https://schema.org",
      "@type": "SportsEvent",
      "name": "Member for a Day: The Quarry at La Quinta",
      "description": "An ultra-rare chance to play the legendary Coachella Valley masterpiece, a Fazio design steeped in exclusivity, dramatic beauty, and pure golf adventure!",
      "startDate": "2025-12-10",
      "endDate": "2025-12-11",
      "location": {
        "@type": "Place",
        "name": "The Quarry at La Quinta",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "La Quinta",
          "addressRegion": "CA",
          "addressCountry": "US"
        }
      },
      "image": "https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/637960658d81c57cd05b01e714fe6892.jpeg",
      "offers": {
        "@type": "Offer",
        "price": "1450",
        "priceCurrency": "USD",
        "availability": "https://schema.org/LimitedAvailability",
        "url": "http://bls-quarryatlaquinta.golfgenius.com",
        "validFrom": "2025-01-01"
      },
      "organizer": {
        "@type": "Organization",
        "name": "Bucket List Golf Series",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
      },
      "performer": {
        "@type": "Organization",
        "name": "Bucket List Golf Series"
      }
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How does registration work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Once registration is open, members are selected on a first-come, first-served basis. Members may select their preferred date. Payment is taken at time of registration, but refunds are issued automatically if the event sells out. Selected members are notified by email when they have been accepted to play. Once the event sells out, additional members are placed on a waitlist. Waitlisted members are chosen by lottery if spots become available due to cancellations."
          }
        },
        {
          "@type": "Question",
          "name": "What is the cancellation policy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Cancellations are allowed up to one week before the event. If this is a last minute signup, cancellation may not be possible. Refunds are processed once the spot is filled, minus a 5% processing fee. Please email refund requests to chris.leary@bucketlistgolf.org."
          }
        },
        {
          "@type": "Question",
          "name": "Who can sign up for the events?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Events are first open to Bucket List Series members on Thousand Greens. If spots remain, registration opens to the public (including Broken Tee Society and other golf groups). Members are welcomed to sign up multiple players, but members must be registered on the Bucket List Series website on Golf Genius. Every effort will be made to pair groups together when registering multiple players."
          }
        },
        {
          "@type": "Question",
          "name": "What's included in the event fees?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Each event is custom-curated for the venue and the event. Event fees usually include greens fees, cart/caddie, meals, tee gifts, pro shop credit, breakfast, lunch, or dinner receptions, etc. Some events may include travel and lodging. Full details are listed in the 'What's Included' section on the Bucket List Series Golf Genius website."
          }
        },
        {
          "@type": "Question",
          "name": "What happens if the event is canceled due to weather?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Weather cancellations are determined at the sole discretion of the golf course. Refunds will be provided whenever applicable. If the event proceeds in extremely bad weather, impacted members will receive first right of refusal for the next year's event at that location."
          }
        },
        {
          "@type": "Question",
          "name": "Are singles welcomed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely! Come as a group, or come as a single and meet other new friends from BLS."
          }
        },
        {
          "@type": "Question",
          "name": "Are there handicap or skill requirements?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Players of all skill levels and handicaps are welcome to register. Pace of play is very important, so we just ask you to keep up with the group in front of you. Net Double-bogey max scoring will be in place to ensure pace of play. A GHIN or other accredited handicap is required to compete in the NET division."
          }
        },
        {
          "@type": "Question",
          "name": "What is not included in the registration fees?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Airfare and lodging are not included in these fees. All costs for golf, meals, and open bar (within course policy) are covered with registration."
          }
        },
        {
          "@type": "Question",
          "name": "Other questions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Contact us anytime at info@bucketlistgolf.org."
          }
        }
      ]
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(eventSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(faqSchema);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How does registration work?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Once registration is open, members are selected on a first-come, first-served basis.</li>
          <li>Members may select their preferred date.</li>
          <li>Payment is taken at time of registration, but refunds are issued automatically if the event sells out.</li>
          <li>Selected members are notified by email when they have been accepted to play.</li>
          <li>Once the event sells out, additional members are placed on a waitlist.</li>
          <li>Waitlisted members are chosen by lottery if spots become available due to cancellations.</li>
        </ul>
      )
    },
    {
      question: "What is the cancellation policy?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Cancellations are allowed up to one week before the event.</li>
          <li>If this is a last minute signup, cancellation may not be possible.</li>
          <li>Refunds are processed once the spot is filled, minus a 5% processing fee.</li>
          <li>Please email refund requests to chris.leary@bucketlistgolf.org.</li>
        </ul>
      )
    },
    {
      question: "Who can sign up for the events?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Events are first open to Bucket List Series members on Thousand Greens.</li>
          <li>If spots remain, registration opens to the public (including Broken Tee Society and other golf groups).</li>
          <li>Members are welcomed to sign up multiple players, but members must be registered on the Bucket List Series website on Golf Genius here.</li>
          <li>Every effort will be made to pair groups together when registering multiple players.</li>
        </ul>
      )
    },
    {
      question: "What's included in the event fees?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Each event is custom-curated for the venue and the event.</li>
          <li>Event fees usually include greens fees, cart/caddie, meals, tee gifts, pro shop credit, breakfast, lunch, or dinner receptions, etc.</li>
          <li>Some events may include travel and lodging.</li>
          <li>Full details are listed in the 'What's Included' section on the Bucket List Series Golf Genius website.</li>
        </ul>
      )
    },
    {
      question: "What happens if the event is canceled due to weather?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Weather cancellations are determined at the sole discretion of the golf course.</li>
          <li>Refunds will be provided whenever applicable.</li>
          <li>If the event proceeds in extremely bad weather, impacted members will receive first right of refusal for the next year's event at that location.</li>
        </ul>
      )
    },
    {
      question: "Are singles welcomed?",
      answer: "Absolutely! Come as a group, or come as a single and meet other new friends from BLS."
    },
    {
      question: "Are there handicap or skill requirements?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Players of all skill levels and handicaps are welcome to register.</li>
          <li>Pace of play is very important, so we just ask you to keep up with the group in front of you.</li>
          <li>Net Double-bogey max scoring will be in place to ensure pace of play.</li>
          <li>A GHIN or other accredited handicap is required to compete in the NET division.</li>
        </ul>
      )
    },
    {
      question: "What is not included in the registration fees?",
      answer: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Airfare and lodging are not included in these fees</li>
          <li>All costs for golf, meals, and open bar (within course policy) are covered with registration</li>
        </ul>
      )
    },
    {
      question: "Other questions?",
      answer: "Contact us anytime at info@bucketlistgolf.org."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[70vh] sm:h-[80vh] lg:h-[85vh] w-full overflow-hidden">
        <img
          src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/637960658d81c57cd05b01e714fe6892.jpeg"
          alt="The Quarry at La Quinta Golf Course"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Logo positioned on the left */}
        <div className="absolute top-6 sm:top-8 lg:top-10 left-4 sm:left-6 lg:left-10 z-10">
          <img
            src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/10d9fc1b609b54364963a5ea239520dd.png"
            alt="Bucket List Golf Logo"
            className="w-24 sm:w-32 lg:w-40 h-auto"
          />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-2 sm:mb-3 font-light tracking-wide">
            Bucket List Golf Presents
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 sm:mb-3 max-w-5xl leading-tight">
            A Member for a Day Experience:
          </h1>
          <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-3 sm:mb-4 max-w-5xl leading-tight">
            The Quarry at La Quinta
          </h2>
          <p className="text-xl sm:text-2xl md:text-3xl text-white/95 mb-4 sm:mb-6 font-medium">
            More Dates Announced Soon!
          </p>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white max-w-4xl leading-relaxed font-light mb-6 sm:mb-8 lg:mb-10 px-2">
            An ultra-rare chance to play the legendary Coachella Valley masterpiece, a Fazio design steeped in exclusivity, dramatic beauty, and pure golf adventure!
          </p>
          <a
            href="http://bls-quarryatlaquinta.golfgenius.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-yellow-400 text-gray-900 px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold hover:bg-yellow-500 transition-colors shadow-2xl whitespace-nowrap cursor-pointer"
          >
            Register for Quarry at La Quinta Event
          </a>
        </div>
      </div>

      {/* Member for a Day Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
          A Member for a Day Experience
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center mb-8 sm:mb-10 lg:mb-12">
          <div>
            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
              This is a Member for a Day experience at the legendary The Quarry at La Quinta—where Fazio's architectural brilliance meets the raw drama of the Coachella Valley. Reserved for members and their closest friends, this day offers 18 holes of golf across the club's storied Championship Course, an experience defined by solitude, scenery, and pure golf.
            </p>
            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
              Only Bucket List Series opens this door for a select group—giving you access to golf as few ever know it.
            </p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-xl">
            <img 
              src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/75d3dcbaae8d329da689b82af72f6f3e.webp" 
              alt="The Quarry at La Quinta Golf Course" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
          <div className="rounded-lg overflow-hidden shadow-2xl order-2 md:order-1">
            <img
              src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/e537da0be21968d0962545ecfdfa887f.jpeg"
              alt="The Quarry Golf Experience"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
              But this round is more than just exclusive; it's meaningful. Every swing supports families through the Center for Childhood, offering hope and help where it's needed most.
            </p>
          </div>
        </div>
      </section>

      {/* Event Details Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-10 lg:mb-12 text-center">
            Event Details
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
              <div className="flex items-start mb-4 sm:mb-6">
                <i className="ri-calendar-line text-2xl sm:text-3xl text-teal-600 mr-3 sm:mr-4 w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Date</h3>
                  <p className="text-base sm:text-lg text-gray-700">More dates announced soon...</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
              <div className="flex items-start mb-4 sm:mb-6">
                <i className="ri-group-line text-2xl sm:text-3xl text-teal-600 mr-3 sm:mr-4 w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Players</h3>
                  <p className="text-base sm:text-lg text-gray-700">Maximum 6 each day</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
              <div className="flex items-start mb-4 sm:mb-6">
                <i className="ri-golf-ball-line text-2xl sm:text-3xl text-teal-600 mr-3 sm:mr-4 w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Package</h3>
                  <p className="text-base sm:text-lg text-gray-700">18 holes on the Championship Course, gourmet lunch, open bar, all-inclusive!</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
              <div className="flex items-start mb-4 sm:mb-6">
                <i className="ri-price-tag-3-line text-2xl sm:text-3xl text-teal-600 mr-3 sm:mr-4 w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Price</h3>
                  <p className="text-base sm:text-lg text-gray-700 font-bold">$1,450 per player</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-10 lg:mb-12 text-center">
          What's Included
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          <div className="flex items-start p-4 sm:p-6 bg-white rounded-lg shadow-md border-l-4 border-teal-600">
            <i className="ri-checkbox-circle-fill text-xl sm:text-2xl text-teal-600 mr-3 sm:mr-4 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center flex-shrink-0"></i>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Championship Round</h3>
              <p className="text-sm sm:text-base text-gray-700">One round (18 holes) on the Championship Course</p>
            </div>
          </div>
          <div className="flex items-start p-4 sm:p-6 bg-white rounded-lg shadow-md border-l-4 border-teal-600">
            <i className="ri-checkbox-circle-fill text-xl sm:text-2xl text-teal-600 mr-3 sm:mr-4 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center flex-shrink-0"></i>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Gourmet Dining</h3>
              <p className="text-sm sm:text-base text-gray-700">Gourmet lunch and open bar</p>
            </div>
          </div>
          <div className="flex items-start p-4 sm:p-6 bg-white rounded-lg shadow-md border-l-4 border-teal-600">
            <i className="ri-checkbox-circle-fill text-xl sm:text-2xl text-teal-600 mr-3 sm:mr-4 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center flex-shrink-0"></i>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Practice Facilities</h3>
              <p className="text-sm sm:text-base text-gray-700">Full use of practice facilities</p>
            </div>
          </div>
          <div className="flex items-start p-4 sm:p-6 bg-white rounded-lg shadow-md border-l-4 border-teal-600">
            <i className="ri-checkbox-circle-fill text-xl sm:text-2xl text-teal-600 mr-3 sm:mr-4 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center flex-shrink-0"></i>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">All-Inclusive Experience</h3>
              <p className="text-sm sm:text-base text-gray-700">Everything you need for an unforgettable day</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-10 lg:mb-12 text-center">
            Experience Gallery
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {/* Large hero image spanning 2 columns */}
            <div className="md:col-span-2 md:row-span-2 rounded-lg overflow-hidden shadow-xl h-64 sm:h-80 md:h-full">
              <img
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/c4eaa0fc1b5a8d7ade37df480ac14280.webp"
                alt="The Quarry at La Quinta Panoramic View"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500 cursor-pointer"
              />
            </div>
            {/* Supporting images */}
            <div className="rounded-lg overflow-hidden shadow-xl h-48 sm:h-64 md:h-auto">
              <img
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/6ff48dcbad8bbe4b9234a45a1d18da4f.jpeg"
                alt="Signature Hole Detail"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500 cursor-pointer"
              />
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl h-48 sm:h-64 md:h-auto">
              <img
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/1286af0af0cd59a5122f22b4673f3c99.jpeg"
                alt="Elevated Tee Box View"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 max-w-4xl mx-auto">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 sm:mb-10 lg:mb-12 text-center">
          Frequently Asked Questions
        </h2>
        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-4 sm:px-6 py-4 sm:py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <h3 className="text-base sm:text-lg font-bold text-gray-900 pr-3 sm:pr-4">{faq.question}</h3>
                <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl sm:text-2xl text-teal-600 flex-shrink-0 w-6 h-6 flex items-center justify-center`}></i>
              </button>
              {openFaq === index && (
                <div className="px-4 sm:px-6 pb-4 sm:pb-5 pt-2">
                  <div className="text-sm sm:text-base text-gray-700 leading-relaxed">{faq.answer}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Ready to Make A Difference Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            Ready to Make A Difference?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white mb-6 sm:mb-8 lg:mb-10 leading-relaxed">
            This event is more than a round – it's a chance to make an impact, and write a story you'll tell forever.
          </p>
          <a
            href="http://bls-quarryatlaquinta.golfgenius.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-teal-600 text-white px-8 sm:px-10 lg:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg lg:text-xl font-bold hover:bg-teal-700 transition-colors shadow-2xl whitespace-nowrap cursor-pointer"
          >
            Register for Quarry at La Quinta
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-10 sm:py-12 lg:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-10 lg:mb-12">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Bucket List Golf</h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-4">
                Exclusive access to America's most coveted golf courses through curated tournaments and events.
              </p>
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.facebook.com/BucketListGolfSeries" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-2xl w-8 h-8 flex items-center justify-center"></i>
                </a>
                <a 
                  href="https://www.instagram.com/BucketListGolfSeries" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-fill text-2xl w-8 h-8 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm sm:text-base">
                <li>
                  <a href="https://bucketlistgolf.org/events" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Upcoming Events
                  </a>
                </li>
                <li>
                  <a href="https://bucketlistgolf.org/bulletin" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Bucket List Bulletin
                  </a>
                </li>
                <li>
                  <a href="https://bucketlistgolf.org/membership" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Membership
                  </a>
                </li>
                <li>
                  <a href="https://bucketlistgolf.org/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="https://bucketlistgolf.org/contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div className="sm:col-span-2 md:col-span-1">
              <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Contact Info</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li className="flex items-start">
                  <i className="ri-mail-line mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <a href="mailto:Info@BucketListGolf.org" className="hover:text-white transition-colors cursor-pointer break-all">
                    Info@BucketListGolf.org
                  </a>
                </li>
                <li className="flex items-start">
                  <i className="ri-phone-line mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <a href="tel:+14132227701" className="hover:text-white transition-colors cursor-pointer">
                    +1 (413) 222-7701
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-gray-400">
            <p>&copy; 2025 Bucket List Golf Series. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
